"use client";

import { useSetAtom } from "jotai";
import type { MapLayerMouseEvent, SymbolLayerSpecification } from "maplibre-gl";
import { RLayer, RSource, useMap } from "maplibre-react-components";
import React, { type ReactElement } from "react";
import { useSites } from "src/hooks/sites";
import { selectedSiteAtom } from "@/store/sites";
import { ThemeTypeEnum, type Site } from "@/types";
import { getThemeColor } from "@/utils";

interface SitesMapLayerStationProps {
  sites: Site[];
  theme: ThemeTypeEnum;
  interactive: boolean;
}

function SitesMapLayerStation({
  sites,
  theme,
  interactive = true,
}: Readonly<SitesMapLayerStationProps>): ReactElement {
  const map = useMap();
  const { getSitesPointFeatureCollection } = useSites(sites);
  const setSelectedSite = useSetAtom(selectedSiteAtom);

  const features = getSitesPointFeatureCollection(sites);
  const color = getThemeColor(theme);
  const source = `source-station-${theme}`;
  const layer = `layer-station-${theme}`;
  const icon = `triangle-${color}`;
  const layout: SymbolLayerSpecification["layout"] = {
    "icon-image": icon,
    "icon-size": [
      "interpolate",
      ["linear"],
      ["zoom"],
      3,
      0.1875,
      10,
      0.375,
      16,
      0.75,
    ],
    "icon-allow-overlap": true,
  };

  const handleClick = (e: MapLayerMouseEvent): void => {
    if (e.features?.[0].properties) {
      const newSite = sites.find((site) => {
        return site.id === e.features?.[0].properties.id;
      });
      if (newSite) setSelectedSite(newSite);
    }
  };

  const handleMouseEnter = (): void => {
    map.getCanvas().style.cursor = "pointer";
  };

  const handleMouseLeave = (): void => {
    map.getCanvas().style.cursor = "";
  };

  return (
    <>
      <RSource
        data={features}
        generateId
        id={source}
        key={source}
        type="geojson"
      />
      <RLayer
        id={layer}
        key={layer}
        onClick={interactive ? handleClick : undefined}
        onMouseEnter={interactive ? handleMouseEnter : undefined}
        onMouseLeave={interactive ? handleMouseLeave : undefined}
        source={source}
        type="symbol"
        layout={layout}
      />
    </>
  );
}

export { SitesMapLayerStation, type SitesMapLayerStationProps };
